import {
    ErrorHandler,
    inject,
    Injectable,
    Injector,
    makeEnvironmentProviders,
    provideAppInitializer,
    type EnvironmentProviders,
} from '@angular/core';
import { ClientErrorLogService, type LogRuntimeErrorInput } from './client-error-log.service';
import { SessionSentinelService } from './session-sentinel.service';
import type { RuntimeErrorReason } from './types/client-error';

/**
 * Browser noise that is not a fault in this application.
 *
 * `ResizeObserver loop` in particular is reported by Chrome for a layout pass that simply needed a
 * second frame; it is harmless, it fires constantly on tables and dialogs, and it would drown the
 * per-session event budget in nothing.
 */
const IGNORED_MESSAGES = [/ResizeObserver loop/i, /^Script error\.?$/i];

/**
 * A failed lazy chunk means the client is holding a bundle the server no longer has, which is the
 * expected shape of a stale service worker after a release rather than a bug in the code.
 */
const CHUNK_LOAD_PATTERN = /ChunkLoadError|Loading chunk \d|dynamically imported module|Importing a module script failed/i;

/**
 * Errors already reported through the Angular handler.
 *
 * Zone.js swallows in-zone exceptions before they reach `window.onerror`, so in practice the two paths
 * are disjoint — but they are disjoint by implementation detail, and one duplicated crash report is
 * worth more to avoid than this set costs.
 */
const reported = new WeakSet<object>();

/**
 * Routes everything Angular catches into the client error log, then hands it to the default handler.
 *
 * Delegating rather than replacing matters: the default handler is what puts the error in the console,
 * and losing that would make local debugging worse in exchange for making production debugging better.
 */
@Injectable()
export class ClientErrorHandler implements ErrorHandler {
    private readonly injector = inject(Injector);
    private readonly delegate = new ErrorHandler();

    handleError(error: unknown): void {
        this.delegate.handleError(error);

        if (error && typeof error === 'object') {
            reported.add(error);
        }

        report(this.injector, { reason: classify(error, 'uncaught-exception'), error });
    }
}

/**
 * Registers global error capture: the Angular `ErrorHandler`, `window.onerror`, `unhandledrejection`
 * and the session sentinel that reports tabs which died without unloading.
 *
 * @returns Providers to add to the application bootstrap.
 */
export function provideClientErrorLogging(): EnvironmentProviders {
    return makeEnvironmentProviders([
        { provide: ErrorHandler, useClass: ClientErrorHandler },
        provideAppInitializer(() => {
            const injector = inject(Injector);

            // Constructing the log service here, rather than on the first error, is deliberate: it has
            // a buffer to flush from the previous session and the flag subscription takes a moment to
            // load. Waiting until something breaks would mean the first failure is never published.
            inject(ClientErrorLogService);
            inject(SessionSentinelService).start();

            if (typeof window === 'undefined') {
                return;
            }

            window.addEventListener('error', event => {
                // Resource load failures arrive as a plain `Event` on the capture phase and are not
                // exceptions. Anything already handled by Angular is skipped.
                if (!(event instanceof ErrorEvent) || (event.error && typeof event.error === 'object' && reported.has(event.error))) {
                    return;
                }

                report(injector, {
                    reason: classify(event.error ?? event.message, 'window-error'),
                    error: event.error,
                    message: event.message,
                    source: { url: event.filename, line: event.lineno, column: event.colno },
                });
            });

            window.addEventListener('unhandledrejection', event => {
                report(injector, {
                    kind: 'unhandled-rejection',
                    reason: 'unhandled-rejection',
                    error: event.reason,
                });
            });
        }),
    ]);
}

/**
 * Hands an error to the log service, resolving it lazily.
 *
 * `ErrorHandler` is constructed before most of the application, so injecting the log service — and
 * through it Firestore, the router and auth — as a constructor dependency creates a cycle that only
 * shows up at runtime.
 */
function report(injector: Injector, input: LogRuntimeErrorInput): void {
    try {
        if (isIgnorable(input)) {
            return;
        }
        injector.get(ClientErrorLogService).logRuntimeError(input);
    } catch (failure) {
        // Reporting an error must never become an error. The console still has the original.
        console.error('[clientErrorHandler] could not report', failure);
    }
}

function isIgnorable(input: LogRuntimeErrorInput): boolean {
    const text = input.message ?? messageOf(input.error);
    return !!text && IGNORED_MESSAGES.some(pattern => pattern.test(text));
}

/**
 * @param fallback - What the error is when it is not a recognisable chunk load failure. Separates an
 *     exception Angular caught from one that only `window.onerror` saw, which is a different bug.
 */
function classify(error: unknown, fallback: RuntimeErrorReason): RuntimeErrorReason {
    const text = `${nameOf(error)} ${messageOf(error)}`;
    return CHUNK_LOAD_PATTERN.test(text) ? 'chunk-load-failed' : fallback;
}

function nameOf(error: unknown): string {
    return error instanceof Error ? error.name : '';
}

function messageOf(error: unknown): string {
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    const message = error && typeof error === 'object' ? (error as { message?: unknown }).message : undefined;
    return typeof message === 'string' ? message : '';
}
